import { useRef, useState, useEffect } from 'react'
import { TrashIcon } from '../components/icons/TrashIcon'
import { BookIcon } from '../components/icons/BookIcon'
import { PencilIcon } from '../components/icons/PencilIcon'
import { FileIcon } from '../components/icons/FileIcon'
import { GlobeIcon } from '../components/icons/GlobeIcon'
import { HeartbeatIcon } from '../components/icons/HeartbeatIcon'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, useApiFetch } from '../lib/api'
import { Card } from '../components/Card'
import { Toast } from '../components/Toast'
import type {
  KnowledgeSource,
  KnowledgeSourceListResponse,
  KnowledgeSourceRef,
} from '../lib/types'
import { Spinner } from '../components/Spinner'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { UnsavedChangesDialog } from '../components/UnsavedChangesDialog'
import {
  KnowledgeSourceForm,
  PulseDot,
  type UrlProbeState,
  type KnowledgeSourceFormValue,
  type KbFieldErrors,
  getKbFormErrors,
} from '../components/KnowledgeSourceForm'
import { useEntityEditorRegistration } from '../hooks/useEntityEditorRegistration'
import { FEATURES } from '../lib/features'

/* ─── Helpers ─── */

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

/* ─── Props ─── */

export interface KnowledgeTabProps {
  agentId: string
  localBindings: KnowledgeSourceRef[]
  globalBindings: KnowledgeSourceRef[]
  onGlobalToggle: (sourceId: string, isEnabled: boolean) => void
  onSourceAdded: (binding: KnowledgeSourceRef) => void
  onSourceRemoved: (sourceId: string) => void
  onDraftDirtyChange?: (dirty: boolean) => void
}

/* ─── Main KnowledgeTab component ─── */

export function KnowledgeTab({
  agentId,
  localBindings,
  globalBindings,
  onGlobalToggle,
  onSourceAdded: _onSourceAdded,
  onDraftDirtyChange,
}: KnowledgeTabProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const apiFetch = useApiFetch()
  const queryClient = useQueryClient()

  /* ── Polling backoff refs ── */
  const localPollMs = useRef(5_000)
  const globalPollMs = useRef(5_000)


  /* ── Delete error state ── */
  const [deleteError, setDeleteError] = useState<{
    sourceId: string
    dependents: Array<{ agentDisplayName: string }>
  } | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<KnowledgeSource | null>(null)

  /* ── Add new source state ── */
  const [isAddingSource, setIsAddingSource] = useState(false)
  const [addForm, setAddForm] = useState<KnowledgeSourceFormValue>({ sourceType: 'document', displayName: '', agentSummary: '', url: '' })
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const [fileError, setFileError] = useState<string | null>(null)
  const [addSourceError, setAddSourceError] = useState<string | null>(null)
  const [kbFieldErrors, setKbFieldErrors] = useState<KbFieldErrors>({})
  const [isSubmittingSource, setIsSubmittingSource] = useState(false)
  /* ── Rename state (pencil icon — opens rename form in right pane) ── */
  const [editingSourceId, setEditingSourceId] = useState<string | null>(null)
  const [renameForm, setRenameForm] = useState<KnowledgeSourceFormValue>({ sourceType: 'document', displayName: '', agentSummary: '', url: '' })
  const [editProbeState, setEditProbeState] = useState<UrlProbeState>('idle')
  const [renameError, setRenameError] = useState<string | null>(null)
  const [renameFieldErrors, setRenameFieldErrors] = useState<KbFieldErrors>({})
  const [urlProbeState, setUrlProbeState] = useState<Record<string, UrlProbeState>>({})
  const [urlWarningToast, setUrlWarningToast] = useState<string | null>(null)
  const [formProbeState, setFormProbeState] = useState<UrlProbeState>('idle')
  /* ── Pending target when switching entities while the draft is dirty ── */
  const [pendingSwitchTarget, setPendingSwitchTarget] = useState<KnowledgeSource | 'add' | null>(null)

  /* ── Queries ── */

  const localQuery = useQuery({
    queryKey: ['knowledge-sources', 'local', agentId],
    queryFn: () =>
      apiFetch<KnowledgeSourceListResponse>(
        `/api/v1/knowledge-sources?scope=Local&ownerAgentId=${agentId}`
      ),
    refetchInterval: (query) => {
      const hasSyncing = (query.state.data?.items ?? []).some((s) => s.isSyncing)
      if (!hasSyncing) {
        localPollMs.current = 5_000
        return false
      }
      const interval = localPollMs.current
      localPollMs.current = Math.min(interval * 2, 30_000)
      return interval
    },
  })

  const globalQuery = useQuery({
    queryKey: ['knowledge-sources', 'global'],
    queryFn: () => apiFetch<KnowledgeSourceListResponse>('/api/v1/knowledge-sources?scope=Global'),
    enabled: FEATURES.globalKnowledgeConfig,
    refetchInterval: (query) => {
      const hasSyncing = (query.state.data?.items ?? []).some((s) => s.isSyncing)
      if (!hasSyncing) {
        globalPollMs.current = 5_000
        return false
      }
      const interval = globalPollMs.current
      globalPollMs.current = Math.min(interval * 2, 30_000)
      return interval
    },
  })

  /* ── Derived ── */

  const allLocalSources = localQuery.data?.items ?? []
  const globalSources = globalQuery.data?.items ?? []
  const localDocs = allLocalSources.filter((s) => s.sourceType === 'Document')
  const globalDocs = globalSources.filter((s) => s.sourceType === 'Document')
  const localWeb = allLocalSources.filter((s) => s.sourceType === 'Web')
  const globalWeb = globalSources.filter((s) => s.sourceType === 'Web')

  const isLoading = localQuery.isLoading || globalQuery.isLoading
  const isError = localQuery.isError || globalQuery.isError

  const editingSource = editingSourceId ? allLocalSources.find((s) => s.id === editingSourceId) : undefined
  const isAddDraftDirty = isAddingSource && (addForm.displayName !== '' || addForm.agentSummary !== '' || addForm.url !== '' || uploadFile !== null)
  const isRenameDraftDirty = !!editingSource && (
    renameForm.displayName !== editingSource.displayName ||
    // A legacy (NULL) source's summary seeds as '' — not dirty on open, but
    // becomes dirty as soon as the user types (Decision 7).
    renameForm.agentSummary !== (editingSource.agentSummary ?? '') ||
    renameForm.url !== (editingSource.url ?? '')
  )
  const isDraftDirty = isAddDraftDirty || isRenameDraftDirty

  useEffect(() => {
    onDraftDirtyChange?.(isDraftDirty)
    return () => onDraftDirtyChange?.(false)
  }, [isDraftDirty, onDraftDirtyChange])

  /** Closes whichever draft editor (add or rename) is currently open, discarding it. */
  function closeDraftEditor() {
    setIsAddingSource(false)
    setUploadFile(null)
    setAddForm({ sourceType: 'document', displayName: '', agentSummary: '', url: '' })
    setAddSourceError(null)
    setKbFieldErrors({})
    setFormProbeState('idle')
    setEditingSourceId(null)
    setRenameForm({ sourceType: 'document', displayName: '', agentSummary: '', url: '' })
    setRenameError(null)
    setRenameFieldErrors({})
  }

  /** Opens the add-new form or a source's rename form, discarding any other open draft. */
  function applySwitch(target: KnowledgeSource | 'add') {
    if (target === 'add') {
      setIsAddingSource(true)
      setEditingSourceId(null)
      setRenameForm({ sourceType: 'document', displayName: '', agentSummary: '', url: '' })
      setRenameError(null)
      setRenameFieldErrors({})
    } else {
      setIsAddingSource(false)
      setEditingSourceId(target.id)
      setRenameForm({
        sourceType: target.sourceType === 'Web' ? 'web' : 'document',
        displayName: target.displayName,
        // Legacy NULL source seeds empty — Save is blocked until typed (Decision 7).
        agentSummary: target.agentSummary ?? '',
        url: target.url ?? '',
      })
      setEditProbeState(urlProbeState[target.id] ?? (
        target.lastProbeStatus === 'ok' ? 'healthy' :
        target.lastProbeStatus === 'warn' ? 'warn' :
        target.lastProbeStatus === 'error' ? 'unreachable' : 'idle'
      ))
      setRenameError(null)
      setRenameFieldErrors({})
    }
  }

  /** Guards switching to a different entity/add-new while the current draft is dirty. */
  function requestSwitch(target: KnowledgeSource | 'add') {
    if (isDraftDirty) {
      setPendingSwitchTarget(target)
    } else {
      applySwitch(target)
    }
  }

  // Entity-editor registry (Knowledge/Agent) — reuses the existing
  // isAddDraftDirty/isRenameDraftDirty snapshot above; page-level Save/Discard
  // wiring (onDraftDirtyChange) is unchanged and stays separate from this registry.
  useEntityEditorRegistration({
    isOpen: () => isAddingSource || !!editingSourceId,
    isDirty: () => isDraftDirty,
    markClean: () => closeDraftEditor(),
  })

  /* ── Binding helpers ── */

  function localIsEnabled(sourceId: string): boolean {
    return localBindings.find((b) => b.knowledgeSourceId === sourceId)?.isEnabled ?? false
  }

  function globalIsEnabled(sourceId: string): boolean {
    return globalBindings.find((b) => b.knowledgeSourceId === sourceId)?.isEnabled ?? false
  }

  /* ── Mutations ── */

  const deleteMutation = useMutation({
    mutationFn: (sourceId: string) =>
      apiFetch<undefined>(`/api/v1/knowledge-sources/${sourceId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['knowledge-sources', 'local', agentId] })
      void queryClient.invalidateQueries({ queryKey: ['knowledge-sources', 'global'] })
      setDeleteError(null)
      setDeleteConfirm(null)
    },
    onError: (err, sourceId) => {
      if (err instanceof ApiError && err.status === 409 && err.code === 'kb.inUse') {
        setDeleteError({
          sourceId,
          dependents: (err.dependents ?? []) as Array<{ agentDisplayName: string }>,
        })
      }
    },
  })

  const createSourceMutation = useMutation({
    mutationFn: async () => {
      if (addForm.sourceType === 'document' && uploadFile) {
        const formData = new FormData()
        formData.append('file', uploadFile)
        formData.append('metadata', JSON.stringify({
          sourceType: 'Document',
          scope: 'Local',
          displayName: addForm.displayName || undefined,
          agentSummary: addForm.agentSummary,
          ownerAgentId: agentId,
        }))
        return apiFetch<KnowledgeSource>('/api/v1/knowledge-sources', {
          method: 'POST',
          body: formData,
        })
      } else if (addForm.sourceType === 'web' && addForm.url) {
        return apiFetch<KnowledgeSource>('/api/v1/knowledge-sources', {
          method: 'POST',
          body: JSON.stringify({
            sourceType: 'Web',
            scope: 'Local',
            url: addForm.url,
            displayName: addForm.displayName || undefined,
            agentSummary: addForm.agentSummary,
            ownerAgentId: agentId,
          }),
          headers: { 'Content-Type': 'application/json' },
        })
      }
      throw new Error('Missing file or URL')
    },
    onSuccess: (source) => {
      void queryClient.invalidateQueries({ queryKey: ['knowledge-sources', 'local', agentId] })
      _onSourceAdded({ knowledgeSourceId: source.id, isEnabled: true })
      setIsAddingSource(false)
      setUploadFile(null)
      setAddForm({ sourceType: 'document', displayName: '', agentSummary: '', url: '' })
      setAddSourceError(null)
      setIsSubmittingSource(false)
      if (source.sourceType === 'Web' && source.lastProbeStatus !== 'ok') {
        setUrlWarningToast(t('knowledgeTab.urlUnreachableWarning'))
      }
      if (source.sourceType === 'Web') {
        setUrlProbeState(prev => ({
          ...prev,
          [source.id]: source.lastProbeStatus === 'ok' ? 'healthy' :
            source.lastProbeStatus === 'warn' ? 'warn' :
            source.lastProbeStatus === 'error' ? 'unreachable' : 'idle',
        }))
      }
    },
    onError: (err) => {
      setAddSourceError(err instanceof Error ? err.message : t('knowledgeTab.errors.failedToCreate'))
      setIsSubmittingSource(false)
    },
  })


  const renameMutation = useMutation({
    mutationFn: ({ id, name, agentSummary, url }: { id: string; name: string; agentSummary: string; url?: string }) =>
      apiFetch<KnowledgeSource>(`/api/v1/knowledge-sources/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ displayName: name, agentSummary, ...(url !== undefined ? { url } : {}) }),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['knowledge-sources', 'local', agentId] })
      setEditingSourceId(null)
      setRenameForm({ sourceType: 'document', displayName: '', agentSummary: '', url: '' })
      setRenameError(null)
      setRenameFieldErrors({})
    },
    onError: (err) => {
      setRenameError(err instanceof Error ? err.message : t('knowledgeTab.errors.failedToRename'))
    },
  })

  const reingestMutation = useMutation({
    mutationFn: (sourceId: string) =>
      apiFetch<KnowledgeSource>(`/api/v1/knowledge-sources/${sourceId}/reingest`, {
        method: 'POST',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['knowledge-sources', 'local', agentId] })
      void queryClient.invalidateQueries({ queryKey: ['knowledge-sources', 'global'] })
    },
  })

  const probeMutation = useMutation({
    mutationFn: (sourceId: string) =>
      apiFetch<KnowledgeSource>(`/api/v1/knowledge-sources/${sourceId}/probe-url`, {
        method: 'POST',
      }),
    onMutate: (sourceId: string) => {
      setUrlProbeState(prev => ({ ...prev, [sourceId]: 'checking' }))
    },
    onSuccess: (source) => {
      setUrlProbeState(prev => ({
        ...prev,
        [source.id]: source.lastProbeStatus === 'ok' ? 'healthy' :
          source.lastProbeStatus === 'warn' ? 'warn' : 'unreachable',
      }))
    },
    onError: (_err, sourceId) => {
      setUrlProbeState(prev => ({ ...prev, [sourceId]: 'idle' }))
    },
  })

  useEffect(() => {
    const { sourceType, url } = addForm
    if (sourceType !== 'web' || !url.trim() ||
        (!url.startsWith('http://') && !url.startsWith('https://'))) {
      setFormProbeState('idle')
      return
    }
    const timer = setTimeout(async () => {
      setFormProbeState('checking')
      try {
        const result = await apiFetch<{ reachable: boolean; status: 'ok' | 'warn' | 'error' }>(
          '/api/v1/knowledge-sources/probe-url',
          { method: 'POST', body: JSON.stringify({ url }) }
        )
        setFormProbeState(result.status === 'ok' ? 'healthy' : result.status === 'warn' ? 'warn' : 'unreachable')
      } catch {
        setFormProbeState('idle')
      }
    }, 600)
    return () => clearTimeout(timer)
  }, [addForm, apiFetch])

  function handleFileChange(file: File, err: string | null) {
    setFileError(err)
    if (!err) {
      setUploadFile(file)
      setKbFieldErrors(e => { const next = { ...e }; delete next.file; return next })
      if (!addForm.displayName) {
        setAddForm(f => ({ ...f, displayName: file.name.replace(/\.[^.]+$/, '') }))
      }
    }
  }

  function handleAddSourceSubmit() {
    setAddSourceError(null)
    const errs = getKbFormErrors(addForm, uploadFile)
    if (Object.keys(errs).length > 0) {
      setKbFieldErrors(errs)
      if (errs.displayName) document.getElementById('kb-name')?.focus()
      else if (errs.agentSummary) document.getElementById('kb-agentsummary')?.focus()
      else if (errs.file) (document.querySelector('.upload-zone') as HTMLElement | null)?.focus()
      else if (errs.url) document.getElementById('kb-url')?.focus()
      return
    }
    setKbFieldErrors({})
    setIsSubmittingSource(true)
    createSourceMutation.mutate()
  }

  /* ── Source row renderer ── */

  function renderSourceRow(source: KnowledgeSource, isLocal: boolean) {
    const isDoc = source.sourceType === 'Document'
    const enabled = isLocal ? localIsEnabled(source.id) : globalIsEnabled(source.id)
    const hasDeleteError = deleteError?.sourceId === source.id
    const isDeleting = deleteMutation.isPending && deleteMutation.variables === source.id
    const isReingesting = reingestMutation.isPending && reingestMutation.variables === source.id
    const isProbing = probeMutation.isPending && probeMutation.variables === source.id

    // Ingestion status
    const isSyncing = source.isSyncing
    const isFailed =
      !source.isIngested && !source.isSyncing && source.ingestionLastError !== null

    // URL health dot state — prefer local override (while probe in flight), fallback to stored value
    const storedProbeState: UrlProbeState =
      source.lastProbeStatus === 'ok' ? 'healthy' :
      source.lastProbeStatus === 'warn' ? 'warn' :
      source.lastProbeStatus === 'error' ? 'unreachable' :
      source.urlReachable === true ? 'healthy' :
      source.urlReachable === false ? 'unreachable' : 'idle'
    const probeState = urlProbeState[source.id] ?? storedProbeState

    return (
      <div key={source.id}>
        <div className="file-row">
          {/* For global sources only: toggle at the beginning */}
          {!isLocal && (
            <label className="toggle" onClick={(e) => e.stopPropagation()}>
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => onGlobalToggle(source.id, e.target.checked)}
              />
              <span className="toggle-track" />
            </label>
          )}

          {/* Source type icon */}
          <div
            className="file-icon-box"
            style={isDoc ? undefined : { background: 'var(--info-bg)' }}
          >
            {isDoc ? <FileIcon /> : <span style={{ color: 'var(--info)' }}><GlobeIcon size={18} /></span>}
          </div>

          {/* Name + meta */}
          <div className="file-info">
            <div className="file-name">{source.displayName}</div>
            <div className="file-meta">
              {isDoc
                ? `${formatBytes(source.fileSize ?? 0)} · Uploaded ${formatDate(source.creationDate)}`
                : source.url ?? ''}
            </div>
          </div>

          {/* Document source health dot */}
          {isDoc && (
            <PulseDot state={
              source.isIngested ? 'healthy' :
              isSyncing ? 'checking' :
              isFailed ? 'unreachable' : 'idle'
            } />
          )}

          {/* Ingestion status badge */}
          {isSyncing && (
            <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: 'var(--warning-bg)', color: 'var(--warning)', whiteSpace: 'nowrap', flexShrink: 0 }}>
              {t('knowledgeTab.status.syncing')}
            </span>
          )}
          {isFailed && (
            <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: 'var(--danger-bg)', color: 'var(--danger)', whiteSpace: 'nowrap', flexShrink: 0 }}>
              {t('knowledgeTab.status.failed')}
            </span>
          )}

          {/* Retry button for failed Document sources */}
          {isFailed && isLocal && isDoc && (
            <button
              type="button"
              onClick={() => reingestMutation.mutate(source.id)}
              disabled={isReingesting}
              className="btn-text kb-edit-btn"
              title={t('common.retry')}
              style={{ fontSize: 11, color: 'var(--warning)' }}
            >
              {isReingesting ? <Spinner size={12} /> : t('common.retry')}
            </button>
          )}

          {/* Web source health dot + probe button */}
          {!isDoc && (
            <>
              <PulseDot state={isProbing ? 'checking' : probeState} />
              <button
                type="button"
                onClick={() => probeMutation.mutate(source.id)}
                disabled={isProbing}
                className="skill-edit-btn"
                title={t('knowledgeTab.probeUrl')}
                aria-label={t('knowledgeTab.probeUrl')}
              >
                <HeartbeatIcon />
              </button>
            </>
          )}

          {/* For local sources: rename and delete buttons */}
          {isLocal && (
            <>
              <button
                type="button"
                onClick={() => requestSwitch(source)}
                className="btn-text kb-edit-btn"
                title={t('knowledgeTab.rename')}
                aria-label={t('knowledgeTab.renameAria', { name: source.displayName })}
              >
                <PencilIcon size={14} />
              </button>
              <button
                type="button"
                onClick={() => {
                  setDeleteError(null)
                  setDeleteConfirm(source)
                }}
                disabled={isDeleting}
                className="skill-remove-btn kb-remove-btn"
                title={t('knowledgeTab.remove')}
                aria-label={t('knowledgeTab.removeAria', { name: source.displayName })}
              >
                {isDeleting ? <Spinner size={14} /> : <TrashIcon size={14} />}
              </button>
            </>
          )}
        </div>

        {/* Ingestion error detail */}
        {isFailed && isLocal && source.ingestionLastError && (
          <div
            style={{
              padding: '4px 16px 6px',
              fontSize: 11,
              color: 'var(--danger)',
              background: 'var(--danger-bg)',
              borderBottom: '1px solid var(--border-light)',
            }}
            title={source.ingestionLastError}
          >
            {source.ingestionLastError.slice(0, 120)}
          </div>
        )}

        {/* Delete error inline */}
        {hasDeleteError && (
          <div
            role="alert"
            style={{
              padding: '8px 16px',
              background: 'var(--danger-bg)',
              borderBottom: '1px solid var(--border-light)',
              fontSize: 12,
              color: 'var(--danger)',
            }}
          >
            {deleteError!.dependents.length > 0
              ? `${t('knowledgeTab.errors.deleteInUse')} ${deleteError!.dependents.map((d) => d.agentDisplayName).join(', ')}`
              : t('knowledgeTab.errors.cannotDelete')}
          </div>
        )}
      </div>
    )
  }

  /* ── Render ── */

  if (isLoading) {
    return (
      <div style={{ padding: 40, display: 'flex', justifyContent: 'center' }}>
        <Spinner size={24} />
      </div>
    )
  }

  if (isError) {
    return (
      <div
        role="alert"
        style={{
          padding: '12px 20px',
          background: 'var(--danger-bg)',
          borderRadius: 8,
          color: 'var(--danger)',
          fontSize: 13,
        }}
      >
        {t('knowledgeTab.failedToLoad')}
      </div>
    )
  }

  /* ── Merged local sources (docs + web) ── */
  const localSources = [...localDocs, ...localWeb]

  return (
    <div>
      {/* ── Local Sources card ── */}
      <Card title={t('knowledgeTab.local.title')} subtitle={t('knowledgeTab.local.subtitle')} noPadding className="editor-split-card">
        <div className="editor-split-body">
          {/* Left: source list */}
          <div style={{ flex: 1, padding: '20px 24px', overflowY: 'auto' }}>
            {localSources.length === 0 ? (
              <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>
                {t('knowledgeTab.noSources')}
              </div>
            ) : (
              <div>
                {localSources.map((s) => renderSourceRow(s, true))}
              </div>
            )}
          </div>

          {/* Separator */}
          <div style={{ width: 1, background: 'var(--border-light)', margin: '20px 0' }} />

          {/* Right: editor pane */}
          <div className="editor-pane-col">

            {/* ── Idle mode ── */}
            {!isAddingSource && !editingSourceId && (
              <div className="editor-pane-empty">
                <BookIcon size={32} />
                <p style={{ fontSize: 13 }}>{t('knowledgeTab.idle')}</p>
                <button
                  type="button"
                  onClick={() => requestSwitch('add')}
                  className="btn btn-primary"
                  style={{ fontSize: 13, padding: '5px 12px' }}
                >
                  {t('knowledgeTab.addNew')}
                </button>
              </div>
            )}

            {/* ── Rename mode ── */}
            {editingSourceId && !isAddingSource && (() => {
              const isWebSource = editingSource?.sourceType === 'Web'
              return (
              <div className="editor-form-wrap">
                <div className="editor-form-scroll" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{t('knowledgeTab.editor.renameTitle')}</h3>
                  <KnowledgeSourceForm
                    mode="rename"
                    idPrefix="kb-rename"
                    value={renameForm}
                    onChange={(patch) => {
                      if ('displayName' in patch) setRenameFieldErrors(e => { const next = { ...e }; delete next.displayName; return next })
                      if ('agentSummary' in patch) setRenameFieldErrors(e => { const next = { ...e }; delete next.agentSummary; return next })
                      if ('url' in patch) setEditProbeState('idle')
                      setRenameForm(f => ({ ...f, ...patch }))
                    }}
                    file={null}
                    onFileChange={() => { /* no-op — rename has no file re-upload */ }}
                    probeState={editProbeState}
                    fieldErrors={renameFieldErrors}
                  />
                  {renameError && (
                    <div role="alert" style={{ padding: 8, background: 'var(--danger-bg)', borderRadius: 4, fontSize: 12, color: 'var(--danger)' }}>
                      {renameError}
                    </div>
                  )}
                </div>
                <div className="editor-form-bar">
                  <button
                    type="button"
                    onClick={() => {
                      setEditingSourceId(null)
                      setRenameForm({ sourceType: 'document', displayName: '', agentSummary: '', url: '' })
                      setRenameError(null)
                      setRenameFieldErrors({})
                    }}
                    className="btn btn-ghost"
                    style={{ fontSize: 12, padding: '5px 12px' }}
                  >
                    {t('knowledgeTab.editor.cancel')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const errs = getKbFormErrors(renameForm, null, { requireFile: false })
                      if (Object.keys(errs).length > 0) {
                        setRenameFieldErrors(errs)
                        return
                      }
                      setRenameFieldErrors({})
                      renameMutation.mutate({
                        id: editingSourceId,
                        name: renameForm.displayName.trim(),
                        agentSummary: renameForm.agentSummary.trim(),
                        ...(isWebSource ? { url: renameForm.url.trim() } : {}),
                      })
                    }}
                    disabled={renameMutation.isPending || Object.keys(renameFieldErrors).length > 0}
                    className="btn btn-primary"
                    style={{ fontSize: 12, padding: '5px 12px' }}
                  >
                    {renameMutation.isPending ? t('knowledgeTab.editor.saving') : t('knowledgeTab.editor.save')}
                  </button>
                </div>
              </div>
              )
            })()}

            {/* ── Add new source mode ── */}
            {isAddingSource && (
              <div className="editor-form-wrap">
                <div className="editor-form-scroll">
                  <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 600 }}>{t('knowledgeTab.editor.addTitle')}</h3>

                  <KnowledgeSourceForm
                    idPrefix="kb"
                    value={addForm}
                    onChange={(patch) => {
                      if ('sourceType' in patch) { setUploadFile(null); setFileError(null); setKbFieldErrors({}) }
                      if ('displayName' in patch) setKbFieldErrors(e => { const next = { ...e }; delete next.displayName; return next })
                      if ('url' in patch) setKbFieldErrors(e => { const next = { ...e }; delete next.url; return next })
                      setAddForm(f => ({ ...f, ...patch }))
                    }}
                    file={uploadFile}
                    fileError={fileError}
                    onFileChange={handleFileChange}
                    probeState={formProbeState}
                    fieldErrors={kbFieldErrors}
                  />

                  {addSourceError && (
                    <div role="alert" style={{ padding: 8, background: 'var(--danger-bg)', borderRadius: 4, fontSize: 12, color: 'var(--danger)', marginBottom: 8 }}>
                      {addSourceError}
                    </div>
                  )}
                </div>
                <div className="editor-form-bar">
                  <button
                    type="button"
                    onClick={() => {
                      setIsAddingSource(false)
                      setUploadFile(null)
                      setAddForm({ sourceType: 'document', displayName: '', agentSummary: '', url: '' })
                      setAddSourceError(null)
                      setKbFieldErrors({})
                      setFormProbeState('idle')
                    }}
                    className="btn btn-ghost"
                    style={{ fontSize: 12, padding: '5px 12px' }}
                  >
                    {t('knowledgeTab.editor.cancel')}
                  </button>
                  <button
                    type="button"
                    onClick={handleAddSourceSubmit}
                    disabled={isSubmittingSource || createSourceMutation.isPending || Object.keys(kbFieldErrors).length > 0}
                    className="btn btn-primary"
                    style={{ fontSize: 12, padding: '5px 12px' }}
                  >
                    {isSubmittingSource || createSourceMutation.isPending ? t('knowledgeTab.editor.saving') : t('knowledgeTab.editor.save')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* ── Global Sources card ── */}
      {/* Hidden while Global Settings → Knowledge Base is preview-only
          (MS-866 AC1): nothing meaningful to show or toggle when global
          source management isn't a real surface yet. */}
      {FEATURES.globalKnowledgeConfig && (
      <Card
        title={t('knowledgeTab.global.title')}
        subtitle={t('knowledgeTab.global.subtitle')}
        actions={
          <button className="btn btn-ghost" style={{ fontSize: 12, padding: '5px 12px' }} onClick={() => navigate('/global-settings/knowledge-base')}>
            {t('common.manageInGlobalSettings')}
          </button>
        }
      >
        {/* Global sources list */}
        {globalDocs.length === 0 && globalWeb.length === 0 ? (
          <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>
            {t('knowledgeTab.noGlobalSources')}
          </div>
        ) : (
          <div>
            {[...globalDocs, ...globalWeb].map((source) => {
              const enabled = globalIsEnabled(source.id)
              const isDoc = source.sourceType === 'Document'

              return (
                <div key={source.id} className="file-row">
                  {/* Icon */}
                  <div
                    className="file-icon-box"
                    style={isDoc ? undefined : { background: 'var(--info-bg)' }}
                  >
                    {isDoc ? <FileIcon /> : <span style={{ color: 'var(--info)' }}><GlobeIcon size={18} /></span>}
                  </div>

                  {/* File info */}
                  <div className="file-info">
                    <div className="file-name">{source.displayName}</div>
                    <div className="file-meta">
                      {isDoc
                        ? `${formatBytes(source.fileSize ?? 0)} · Uploaded ${formatDate(source.creationDate)}`
                        : source.url ?? ''}
                    </div>
                  </div>

                  {/* Toggle on the right (last child) */}
                  <label className="toggle" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={enabled}
                      onChange={(e) => onGlobalToggle(source.id, e.target.checked)}
                    />
                    <span className="toggle-track" />
                  </label>
                </div>
              )
            })}
          </div>
        )}
      </Card>
      )}

      {deleteConfirm && (
        <ConfirmDialog
          title={t('knowledgeTab.deleteDialog.title', { name: deleteConfirm.displayName })}
          message={t('knowledgeTab.deleteDialog.message')}
          confirmLabel={t('knowledgeTab.deleteDialog.confirm')}
          danger
          onConfirm={() => deleteMutation.mutate(deleteConfirm.id)}
          onCancel={() => setDeleteConfirm(null)}
        />
      )}

      {urlWarningToast && (
        <Toast
          message={urlWarningToast}
          variant="warning"
          onDismiss={() => setUrlWarningToast(null)}
        />
      )}

      {pendingSwitchTarget && (
        <UnsavedChangesDialog
          onConfirm={() => {
            const target = pendingSwitchTarget
            setPendingSwitchTarget(null)
            applySwitch(target)
          }}
          onCancel={() => setPendingSwitchTarget(null)}
        />
      )}
    </div>
  )
}
